import { type TermsAcceptancesListViewModel } from '@/features/account/terms/TermsAcceptancesList/TermsAcceptancesListViewModel'
import { observer } from 'mobx-react-lite'
import { useRootStore } from '@/contexts/root-store-context'
import { Button } from '@/components/Button'
import { type TermsOfUseAcceptance } from '@/features/account/terms/TermsOfUseAcceptance'
import { type TermsOfUseDocument } from '@/features/account/terms/TermsOfUseDocument'
import { dateFormatters } from '@/utils/date-util'
import { ViewAcceptedTermsDialogContainer } from '@/features/account/terms/ViewAcceptedTermsDialog/ViewAcceptedTermsDialogContainer'
import { useScopedViewModel } from '@/hooks/view-model-hooks'
import { ViewAcceptedTermsDialogViewModel } from '@/features/account/terms/ViewAcceptedTermsDialog/ViewAcceptedTermsDialogViewModel'
import {
  EmptyState,
  EmptyStateActions,
  EmptyStateDescription,
  EmptyStateIconDecoration,
  EmptyStateTitle,
} from '@/components/EmptyState'
import { AcceptanceListContainer } from '@/features/account/components/AcceptanceList/AcceptanceListContainer'
import { AcceptanceListItem } from '@/features/account/components/AcceptanceList'

export interface TermsAcceptancesListContainerProps {
  vm: TermsAcceptancesListViewModel
  'aria-labelledby': string
}

export const TermsAcceptancesListContainer = observer(
  function TermsAcceptancesListContainer({
    vm,
    ...rest
  }: TermsAcceptancesListContainerProps) {
    const viewTermsVm = useScopedViewModel(({ account }, needs) => {
      const { internal } = needs({ internal: account.internal() })
      return new ViewAcceptedTermsDialogViewModel(internal.termsOfUseStore)
    })

    return (
      <>
        <AcceptanceListContainer
          {...rest}
          scroller={vm.termsAcceptancesScroller}
          renderEmptyState={() => <EmptyAgreementsContainer vm={vm} />}
        >
          {(item) => (
            <TermsOfUseAcceptanceListItemContainer
              key={item.termsOfUseId}
              acceptance={item}
              termsOfUse={vm.getTermsOfUse(item.termsOfUseId)}
              onAction={() => viewTermsVm.view(item.termsOfUseId)}
            />
          )}
        </AcceptanceListContainer>
        <ViewAcceptedTermsDialogContainer vm={viewTermsVm} />
      </>
    )
  },
)

interface EmptyAgreementsContainerProps {
  vm: TermsAcceptancesListViewModel
}

const EmptyAgreementsContainer = observer(function EmptyAgreementsContainer({
  vm,
}: EmptyAgreementsContainerProps) {
  const { account } = useRootStore()
  return (
    <EmptyState>
      <EmptyStateIconDecoration icon="file" />
      <EmptyStateTitle>No agreements</EmptyStateTitle>
      <EmptyStateDescription>
        Once you've agreed to our terms, the acceptance record will appear here.
      </EmptyStateDescription>
      <EmptyStateActions>
        <Button
          iconLeft="view"
          label="View terms"
          onPress={() => account.ensureTermsAcceptance(vm.workspace.id)}
        />
      </EmptyStateActions>
    </EmptyState>
  )
})

interface TermsOfUseAcceptanceListItemContainerProps {
  acceptance: TermsOfUseAcceptance
  termsOfUse: TermsOfUseDocument
  onAction?: () => unknown
}

const TermsOfUseAcceptanceListItemContainer = observer(
  function TermsOfUseAcceptanceListItem({
    acceptance,
    termsOfUse,
    onAction,
  }: TermsOfUseAcceptanceListItemContainerProps) {
    const textValue = `Terms of use published ${dateFormatters.dateLong.format(termsOfUse.dateCreated)}, accepted ${dateFormatters.dateLong.format(acceptance.acceptedAt)}`

    return (
      <AcceptanceListItem
        title={`Terms of use published ${dateFormatters.dateLong.format(termsOfUse.dateCreated)}`}
        subtitle={`Accepted ${dateFormatters.dateLong.format(acceptance.acceptedAt)}`}
        textValue={textValue}
        onAction={onAction}
      />
    )
  },
)
